import type { Nullable, ProductEventDataList } from 'corgi-types';
import type { Sponsored } from 'graphql-ts-types';
import { AdditionalServiceType } from 'graphql-ts-types';

import type { ArticleCouponProps } from '../../article-price/components/ArticlePrice';
import type { ArticleTracking } from '../types/Article';
import { formatNumberPrice } from '../utils/format-price';

export interface Assembly {
  price: number;
  sku: string;
  title: string;
}

interface ProductEventDataArgs {
  tracking: Partial<ArticleTracking>;
  list?: ProductEventDataList;
  position?: number;
  quantity?: number;
  dyDecisionId?: string;
  dySlotId?: string;
  sponsored?: Nullable<Sponsored>;
  tax?: number;
  services?: AdditionalServiceType[];
  couponDiscountAmount?: Nullable<number>;
  couponCode?: Nullable<string>;
  hasMinPrice?: boolean;
}

const omitUndefined = <T extends object>(obj: T) => {
  const result = {} as Partial<T>;

  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }

  return result;
};

export const getProductEventData = ({
  position,
  tracking: {
    productSimpleSKU,
    productName,
    productPrice,
    productBrand,
    categoryBreadcrumb,
    variantName,
    productDeliveryTime,
    resultId,
    productCampaigns,
    productLabels,
    productConfigSKU,
    productShippingCharge,
    productServiceCharge,
    productEfficiencyValue,
    shopId,
    placement,
  },
  quantity,
  list,
  dyDecisionId,
  dySlotId,
  sponsored,
  tax,
  services,
  couponCode,
}: ProductEventDataArgs) =>
  omitUndefined({
    id: productSimpleSKU,
    name: productName,
    price: productPrice / 100,
    brand: productBrand,
    category: categoryBreadcrumb,
    variant: variantName,
    deliveryTime: productDeliveryTime,
    resultId,
    campaigns: productCampaigns,
    labels: productLabels,
    quantity,
    position,
    list,
    dyDecisionId,
    dySlotId,
    productConfigSKU,
    productShippingCharge,
    productServiceCharge,
    productEfficiencyValue,
    shopId,
    placement,
    services,
    ...(sponsored !== undefined && { isAds: !!sponsored }),
    ...(tax && { tax: tax / 100 }),
    hasCoupon: !!couponCode ? 'yes' : 'no',
  });

export const getAssemblyServiceEventData = (
  services?: AdditionalServiceType[],
  assembly?: Assembly
) =>
  services?.includes(AdditionalServiceType.Assembly) && assembly
    ? [
        {
          item_name: assembly.title,
          item_id: assembly.sku,
          price: assembly.price / 100,
        },
      ]
    : [];

const getProductCommonEventData = ({
  tracking: {
    productSimpleSKU,
    productName,
    productPrice,
    productBrand,
    productCategories,
    productLabels,
    variantName,
    productDeliveryTime,
    productCampaigns,
    productConfigSKU,
    resultId,
  },
  couponDiscountAmount,
  couponCode,
  hasMinPrice,
}: ProductEventDataArgs) => ({
  item_name: productName,
  item_id: productSimpleSKU,
  price: productPrice / 100,
  item_brand: productBrand,
  item_category: productCategories?.[0],
  item_category2: productCategories?.[1],
  item_category3: productCategories?.[2],
  item_category4: productCategories?.[3],
  labels: productLabels,
  item_variant: variantName,
  delivery_time: productDeliveryTime,
  campaigns: productCampaigns,
  has_coupon: !!couponCode ? 'yes' : 'no',
  ...(couponCode && { coupon: couponCode }),
  ...(couponDiscountAmount && { discount: formatNumberPrice(couponDiscountAmount) }),
  product_config_sku: productConfigSKU,
  result_id: resultId,
  ...(typeof hasMinPrice === 'boolean' && { has_min_price: hasMinPrice ? 'yes' : 'no' }),
});

export const getProductViewEventData = (productEventData: ProductEventDataArgs) => {
  const {
    sponsored,
    list,
    position,
    dySlotId,
    dyDecisionId,
    tracking: { isHome24Tipp },
  } = productEventData;

  return omitUndefined({
    ...getProductCommonEventData(productEventData),
    is_ads: sponsored ? true : undefined,
    item_list_name: list,
    index: position,
    dy_decision_id: dyDecisionId,
    dy_slot_id: dySlotId,
    is_home24_tipp: isHome24Tipp,
  });
};

export const getProductClickEventData = (productEventData: ProductEventDataArgs) => {
  const {
    sponsored,
    list,
    position,
    dySlotId,
    dyDecisionId,
    tracking: {
      productShippingCharge,
      productServiceCharge,
      productEfficiencyValue,
      shopId,
      isHome24Tipp,
    },
  } = productEventData;

  return omitUndefined({
    ...getProductCommonEventData(productEventData),
    is_ads: sponsored ? true : undefined,
    item_list_name: list,
    index: position,
    dy_decision_id: dyDecisionId,
    dy_slot_id: dySlotId,
    product_shipping_charge: productShippingCharge,
    product_service_charge: productServiceCharge,
    product_efficiency_value: productEfficiencyValue,
    shop_id: shopId,
    is_home24_tipp: isHome24Tipp,
  });
};

export const getProductDetailViewEventData = (
  productEventData: ProductEventDataArgs & { coupon?: Nullable<ArticleCouponProps> }
) => {
  const {
    tracking: { shopId, isHome24Tipp },
    coupon,
  } = productEventData;

  return omitUndefined({
    ...getProductCommonEventData({
      ...productEventData,
      couponCode: coupon?.code,
      couponDiscountAmount: coupon?.discount,
    }),
    shop_id: shopId,
    is_home24_tipp: isHome24Tipp,
  });
};

export const getProductAddEventData = (productEventData: ProductEventDataArgs) => {
  const {
    tracking: {
      productShippingCharge,
      productServiceCharge,
      productEfficiencyValue,
      placement,
      shopId,
      isHome24Tipp,
    },
    quantity,
    services,
    tax,
  } = productEventData;

  return omitUndefined({
    ...getProductCommonEventData(productEventData),
    quantity,
    product_shipping_charge: productShippingCharge,
    product_service_charge: productServiceCharge,
    product_efficiency_value: productEfficiencyValue,
    placement,
    services,
    tax: tax / 100,
    shop_id: shopId,
    is_home24_tipp: isHome24Tipp,
  });
};

export const getProductRemoveEventData = (productEventData: ProductEventDataArgs) => {
  const {
    tracking: { productShippingCharge, productServiceCharge, productEfficiencyValue, shopId },
    quantity,
    services,
    tax,
  } = productEventData;

  return omitUndefined({
    ...getProductCommonEventData(productEventData),
    quantity,
    product_shipping_charge: productShippingCharge,
    product_service_charge: productServiceCharge,
    product_efficiency_value: productEfficiencyValue,
    services,
    tax: tax / 100,
    shop_id: shopId,
  });
};

export const getProductWishlistEventData = (productEventData: ProductEventDataArgs) => {
  const {
    quantity,
    tracking: { productShippingCharge, productServiceCharge, productEfficiencyValue, shopId },
  } = productEventData;

  return omitUndefined({
    ...getProductCommonEventData(productEventData),
    quantity,
    product_shipping_charge: productShippingCharge,
    product_service_charge: productServiceCharge,
    product_efficiency_value: productEfficiencyValue,
    shop_id: shopId,
  });
};
